import { Client, IMessage } from '@stomp/stompjs';

import { formatTopicId, formatTopicPath } from '../utils';
import { ISocketTopic } from './types';

export enum EPublicMessageType {
  NOTIFICATION = 'NOTIFICATION',
  ONE_CLICK = 'ONE_CLICK'
}

export interface IPublicNotificationMessageData {
  T: number; // time
}

export interface IPublicNotificationMessage {
  M: EPublicMessageType.NOTIFICATION;
  D: IPublicNotificationMessageData;
}

export interface IPublicOneClickMessage {
  M: EPublicMessageType.ONE_CLICK;
  D: IPublicNotificationMessageData;
}

export type IPublicMessage = IPublicNotificationMessage | IPublicOneClickMessage;

export interface IPublicReceiver {
  (payload: IPublicMessage | undefined): void;
}

interface ISubscribeParams {
  tenantId: string;
  receiver: IPublicReceiver;
}

interface IProps {
  client: Client;
}

class PublicTopic implements ISocketTopic {
  private client: Client;
  private receivers: Record<string, Set<IPublicReceiver> | undefined> = {};

  constructor(props: IProps) {
    const { client } = props;

    this.client = client;
  }

  public subscribe = (params: ISubscribeParams) => {
    const { tenantId, receiver } = params;

    const { subPath, topicId } = this.mapTopicIds(tenantId);

    if (!this.client) {
      return;
    }

    if (this.receivers[subPath]) {
      this.receivers[subPath]?.add(receiver);
    } else {
      this.receivers[subPath] = new Set([receiver]);

      try {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: topicId
        });
      } catch (err) {}
    }
  };

  public unsubscribe = (params: ISubscribeParams) => {
    const { tenantId, receiver } = params;

    const { subPath, topicId } = this.mapTopicIds(tenantId);

    if (!this.client) {
      return;
    }

    this.receivers[subPath]?.delete(receiver);

    if (this.receivers[subPath]?.size === 0) {
      try {
        this.client?.unsubscribe(topicId);
      } catch (err) {}

      this.receivers[subPath] = undefined;
    }
  };

  public reconnect = () => {
    const { receivers } = this;

    if (receivers) {
      Object.keys(receivers).forEach((subPath) => {
        this.client.subscribe(formatTopicPath(subPath), (message) => this.onReceive(subPath, message), {
          id: formatTopicId(subPath)
        });
      });
    }
  };

  public clearSubscriptions = () => {
    this.receivers = {};
  };

  private onReceive = (id: string, message: IMessage) => {
    const receivers = this.receivers[id];

    if (!receivers || !message) {
      return;
    }

    const payload = JSON.parse(message.body) as IPublicMessage;

    receivers.forEach((receiver) => receiver(payload));
  };

  private mapTopicIds = (tenantId: string) => {
    const subPath = `public/${tenantId}`;

    return {
      subPath,
      topicId: formatTopicId(subPath)
    };
  };
}

export default PublicTopic;
